import { ListItem, ListItemIcon, ListItemText, Tooltip } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { ChevronRight as ChevronRightIcon } from "@material-ui/icons/";
import clsx from "clsx";
import { NavLink } from "react-router-dom";
import SIDEBAR_COLORS from "./sidebarTheme";

const styles = (theme) => ({
  listItem: {
    borderRadius: "4px",
    margin: "1px 4px",
    padding: "2px 6px",
    minHeight: "22px",
    display: "flex",
    alignItems: "center",
    transition: "background-color 0.2s ease",
    color: SIDEBAR_COLORS.textDefault,
    "&:hover": {
      backgroundColor: SIDEBAR_COLORS.hoverBg,
    },
  },
  activeListItem: {
    backgroundColor: `${SIDEBAR_COLORS.activeBg} !important`,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: `${SIDEBAR_COLORS.activeHoverBg} !important`,
    },
    "& .MuiListItemIcon-root": {
      color: "#ffffff",
    },
    "& .MuiListItemText-primary": {
      color: "#ffffff",
      fontWeight: 500,
    },
  },
  listItemIcon: {
    color: "#ffffff !important",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    overflow: "hidden",
    "& .MuiSvgIcon-root": {
      color: "#ffffff !important",
    },
  },
  iconVisible: {
    opacity: 1,
    minWidth: "14px",
    maxWidth: "14px",
    transition: theme.transitions.create(["opacity", "min-width", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  iconHidden: {
    opacity: 0,
    minWidth: "0px",
    maxWidth: "0px",
    transition: theme.transitions.create(["opacity", "min-width", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  listItemText: {
    margin: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    transition: "opacity 0.2s ease, max-width 0.2s ease, margin-left 0.2s ease",
    "& span": {
      fontSize: "0.65rem",
      fontWeight: 500,
      lineHeight: 1.2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  textVisible: {
    opacity: 1,
    maxWidth: "220px",
    marginLeft: "0px",
  },
  textHidden: {
    opacity: 0,
    maxWidth: "0px",
    marginLeft: "0px",
  },
  nestedItem: {
    borderRadius: "0px !important",
    paddingLeft: "9px",
    paddingRight: "6px",
    paddingTop: "3px",
    paddingBottom: "3px",
    margin: "2px 4px",
    minHeight: "25px",
    backgroundColor: "transparent",
    width: "calc(100% - 8px)",
    boxSizing: "border-box",
    overflow: "hidden",
    "&:hover": {
      backgroundColor: SIDEBAR_COLORS.hoverBg,
    },
    "& .MuiTypography-root": {
      fontSize: "11px !important",
      fontWeight: "500 !important",
      lineHeight: "1.3 !important",
    },
    "& span": {
      fontSize: "11px !important",
      fontWeight: "500 !important",
      lineHeight: "1.3 !important",
    },
  },
  nestedActiveItem: {
    backgroundColor: `${SIDEBAR_COLORS.activeBg} !important`,
    borderRadius: "0px !important",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: `${SIDEBAR_COLORS.activeHoverBg} !important`,
    },
    "& .MuiListItemIcon-root": {
      color: "#ffffff",
    },
    "& span": {
      color: "#ffffff",
      fontWeight: 500,
    }
  },
  iconWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "14px",
    height: "14px",
  },
});

const SidebarItems = ({
  classes,
  item,
  handleDrawerClose,
  drawerOpen,
  selectedRoute,
  isNested = false,
  hasSubItems = false,
}) => {
  const { url, label: name, icon: Icon, whiteImg } = item;

  const getItemClasses = () => {
    const baseClasses = [classes.listItem];

    if (isNested) {
      baseClasses.push(classes.nestedItem);
      if (selectedRoute) {
        baseClasses.push(classes.nestedActiveItem);
      }
    } else if (selectedRoute) {
      baseClasses.push(classes.activeListItem);
    }

    return clsx(baseClasses);
  };

  const getIconClasses = () => {
    return clsx(
      classes.listItemIcon,
      drawerOpen ? classes.iconHidden : classes.iconVisible
    );
  };

  const renderIcon = () => {
    const iconSize = 'w-3 h-3';

    if (whiteImg) {
      return (
        <div className={classes.iconWrapper}>
          <img
            src={whiteImg}
            alt={name}
            className={iconSize}
            style={{
              filter: "brightness(0) invert(1) contrast(1000%)",
              opacity: 1,
            }}
          />
        </div>
      );
    }

    if (Icon) {
      return (
        <div className={classes.iconWrapper}>
          <Icon
            style={{ color: "#ffffff", opacity: 1 }}
            className={clsx(
              isNested ? 'text-xs' : 'text-sm'
            )}
          />
        </div>
      );
    }

    return null;
  };

  const iconElement = renderIcon();

  return (
    <Tooltip
      title={!drawerOpen ? name : ""}
      arrow
      placement="right"
      PopperProps={{
        modifiers: {
          offset: {
            enabled: true,
            offset: '0, 5',
          },
        },
      }}
    >
      <NavLink
        to={url}
        style={{ textDecoration: "none", color: "inherit" }}
        activeClassName=""
        isActive={() => selectedRoute}
      >
        <ListItem
          button
          className={getItemClasses()}
          onClick={handleDrawerClose}
          disableGutters
        >
          {iconElement && (
            <ListItemIcon className={getIconClasses()}>
              {iconElement}
            </ListItemIcon>
          )}
          <ListItemText
            primary={name}
            className={clsx(
              classes.listItemText,
              drawerOpen ? classes.textVisible : classes.textHidden
            )}
            primaryTypographyProps={{
              noWrap: true,
              variant: isNested ? "body2" : "body1"
            }}
          />
          {hasSubItems && (
            <ChevronRightIcon style={{ color: "#ffffff", fontSize: "14px", marginLeft: "auto" }} />
          )}
        </ListItem>
      </NavLink>
    </Tooltip>
  );
};

export default withStyles(styles)(SidebarItems);